import React, { memo } from "react";
import { isNull, round } from "lodash";
import Grid from "@mui/material/Grid";
import Checkbox from "@mui/material/Checkbox";
import { useDispatch, useSelector } from "react-redux";
import Tooltip from "@mui/material/Tooltip";
import CustomDropdown from "../../../components/CustomDropdown";
import CustomInput from "../../../components/CustomInput";
import {
  isAdjQtyValid,
  prettyNumericRound,
  getFormattedNumber,
  singleValue,
} from "../../../utils";
import { SCOPES } from "../../../permissions/permissions-map";
import useStableCallback from "../../../hooks/useStableCallback";
import {
  showNotifier,
  setAdjQtyError,
  setPrevAdjQty,
} from "../../../store/globalSlice";
import NumericLabel from "../../../components/NumericLabel";
import CustomTierChip from "../CustomTierChip";
import { validPogItem } from "../../../utils/itemValidator";
import { isItemDepartment } from "../../../utils/deptValidator";
import usePermissions from "../../../hooks/usePermissions";

const CustomItemSx = {
  fontSize: "0.75rem",
  paddingTop: "0px !important",
  alignSelf: "center",
  marginTop: "5px",
  marginBottom: "5px",
  paddingLeft: "10px",

  "& .MuiCheckbox-root": {
    padding: "0px",
    paddingRight: "5px",
  },
  "& .item-title": {
    display: "inline-flex",
    verticalAlign: "unset",

    "& .item-name": {
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      fontWeight: 600,
    },
  },
  "& .truncated-vendor-name": {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  "& .cost-value": {
    display: "flex",
    alignItems: "center",

    "& .cost-spacing": { marginLeft: "0.5rem" },
  },
  "& .bold-label": { fontWeight: 500 },
};

let timer, correctValueTimer;
const AutoStepTime = 150;
const AutoInputUpperLimit = 9999;
const AutoProps = (adjQtyError) => ({
  min: 0,
  max: 9999,
  style: { backgroundColor: adjQtyError ? "rgba(255, 0, 0, 0.7)" : "" },
});

const CollapsedItemView = ({ data, handleProcessData, freezeItem = false }) => {
  const dispatch = useDispatch();
  const { hasPermission } = usePermissions();
  const prevAdjQty = useSelector((store) => store.global.prevAdjQty);
  const adjQtyError = useSelector((store) => store.global.adjQtyError);
  const store = useSelector((store) => store.global.store);

  const isPartOfItemsDept = isItemDepartment(data);

  const handleChange = useStableCallback((field) => (event) => {
    const value =
      field === "selection" ? event?.target?.checked : event?.target?.value;
    handleProcessData({ field, value });
  });

  const handleShowAdjQtyError = useStableCallback((number = 0) => {
    timer = setTimeout(() => {
      dispatch(
        showNotifier({
          severity: "error",
          message: isAdjQtyValid(number, isPartOfItemsDept)
            ? "Invalid quantity value"
            : `Quantity value of ${number} is not allowed`,
        })
      );
      dispatch(setAdjQtyError(data?._id));
    }, AutoStepTime);
  });

  const correctAdjQtyValue = useStableCallback((number) => {
    clearTimeout(correctValueTimer);
    correctValueTimer = setTimeout(() => {
      handleProcessData({ field: "AdjQty", value: number });
    }, AutoStepTime);
  });

  const handleAdjQty = useStableCallback((event) => {
    const packValues = data?.BreakPackType?.split("F") || [];
    const isFPack = packValues.length === 2;
    const stepper = isFPack ? packValues[1] : 0;
    let newValue = event?.target?.value;
    clearTimeout(timer);
    clearTimeout(correctValueTimer);
    if (!prevAdjQty) {
      dispatch(setPrevAdjQty(data?.AdjQty));
    }
    if (adjQtyError) {
      dispatch(setAdjQtyError(null));
    }
    const currentValue = !isNull(newValue) ? Number(newValue) : null;
    newValue = currentValue;
    if (currentValue > AutoInputUpperLimit || isNaN(currentValue)) {
      return event.preventDefault();
    }
    handleProcessData({ field: "AdjQty", value: newValue });
    if (stepper) {
      if (!isNaN(newValue)) {
        if (
          newValue % stepper === 0 &&
          isAdjQtyValid(newValue, isPartOfItemsDept)
        ) {
          correctAdjQtyValue(newValue);
          dispatch(setAdjQtyError(null));
          dispatch(setPrevAdjQty(null));
        } else {
          handleShowAdjQtyError(newValue);
        }
      } else {
        handleShowAdjQtyError();
      }
    } else {
      if (!isAdjQtyValid(newValue, isPartOfItemsDept)) {
        handleShowAdjQtyError(newValue);
      }
    }
    return true;
  });

  const autoInputProps = AutoProps(
    adjQtyError === data?._id ? adjQtyError : null
  );

  const selectedNo = data?.SelectedItem !== singleValue.yes;

  const blockItem =
    freezeItem ||
    store?.pending ||
    store?.isArchived ||
    store?.isCompleted ||
    store?.dataguideInProgress ||
    !hasPermission({ scopes: [SCOPES.canEdit] }) ||
    !validPogItem(data);

  return (
    <Grid
      container
      item
      xs
      sx={CustomItemSx}
      alignItems="center"
      justifyContent="space-between"
    >
      <Grid item xs="auto">
        <Checkbox
          size="small"
          disableRipple
          checked={data?.selection}
          disabled={blockItem || adjQtyError}
          onClick={handleChange("selection")}
        />
      </Grid>
      <Grid item xs="auto" md={1.5} lg={1.5} className="item-title" mr={1}>
        <Tooltip title={data?.ItemDescription} arrow>
          <span className="item-name">{data?.ItemDescription}</span>
        </Tooltip>
      </Grid>
      <Grid item xs="auto" mr={1}>
        <span className="bold-label">Sel: </span>
        <CustomDropdown
          value={data?.SelectedItem}
          inputsx={{ fontSize: "10px" }}
          onChange={handleChange("SelectedItem")}
          disabled={blockItem || adjQtyError || isPartOfItemsDept}
        >
          <option value="N">N</option>
          <option value="Y">Y</option>
          <option value="E">E</option>
        </CustomDropdown>
      </Grid>
      <Grid item xs="auto" md={0.45} lg={0.5} mr={1}>
        {data?.StoreItemId}
      </Grid>
      <Grid
        item
        xs="auto"
        md={1.5}
        lg={2}
        className="truncated-vendor-name"
        mr={1}
      >
        <Tooltip title={data?.VendorName} arrow>
          <span>{data?.VendorName}</span>
        </Tooltip>
      </Grid>
      <Grid item xs="auto" md={0.72} lg={0.6} mr={1}>
        {data?.Restricted === singleValue.yes ? "Yes" : "No"}
      </Grid>
      <Grid item xs="auto" md={0.55} lg={0.6} mr={1}>
        {data?.Hazmat === singleValue.yes ? "Yes" : "No"}
      </Grid>
      <Grid item xs="auto" md={0.3} lg={0.5} mr={1}>
        {data?.BreakPackType}
      </Grid>
      <Grid item xs="auto" md={0.3} lg={0.5} mr={1}>
        {isPartOfItemsDept ? (
          "-"
        ) : (
          <NumericLabel isSeparator value={round(data?.RecQty || 0, 2)} />
        )}
      </Grid>
      <Grid item xs="auto" md={1.2} lg={1.2} className="cost-value" mr={1}>
        <CustomTierChip tier={data?.Tier} />
        <span className="cost-spacing">
          ${getFormattedNumber(prettyNumericRound(data?.PogCost || 0))}
        </span>
      </Grid>
      <Grid item xs="auto" md={1} lg={1} mr={1}>
        ${getFormattedNumber(prettyNumericRound(data?.AdjCost || 0))}
      </Grid>
      <Grid item xs="auto" md={1} lg={1} mr={1}>
        ${getFormattedNumber(prettyNumericRound(data?.ExtendedCost || 0))}
      </Grid>
      <Grid item xs="auto" mr={2}>
        <CustomInput
          sx={{ width: "35px" }}
          value={data?.AdjQty || 0}
          onChange={handleAdjQty}
          inputProps={autoInputProps}
          disabled={
            blockItem ||
            (adjQtyError && data?._id !== adjQtyError) ||
            selectedNo
          }
        />
      </Grid>
    </Grid>
  );
};

export default memo(CollapsedItemView);
